import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type { ProfileGroup } from '@/types/risk-profile';
import { useRelationshipTypesStore } from './relationshipTypes';
import { useRiskProfileManager } from '@/composables/useRiskProfileManager';
import api from '@/api';

export const useProfilesStore = defineStore('profiles', () => {
    const relationshipTypesStore = useRelationshipTypesStore();

    const profileManager = useRiskProfileManager({
        fetchData: (params) => api.accountAdmin.getProfileGroups(params),
        fetchOne: api.accountAdmin.getProfileGroup,
        create: api.accountAdmin.createRiskModel,
        createDraft: api.accountAdmin.createDraftInstance,
        promote: api.accountAdmin.promoteToProduction,
        activate: api.accountAdmin.activateRiskModel,
        deactivate: api.accountAdmin.deactivateRiskModel,
        setAsProduction: api.accountAdmin.setReleaseAsProduction,
        // discardDraft: api.accountAdmin.discardDraft,
        setDesignated: api.accountAdmin.setDesignatedInstance,
        delete: api.accountAdmin.deleteProfileGroup,
    });

    const activeTemplates: Ref<ProfileGroup[]> = ref([]);

    async function fetchActiveTemplates(): Promise<void> {
        try {
            activeTemplates.value = await api.accountAdmin.getActiveRiskModelTemplates();
        } catch (err) {
            console.error("Failed to fetch active templates:", err);
            activeTemplates.value = [];
        }
    }

    // Override the default fetchData to include other necessary data for this specific view
    async function initialLoad(): Promise<void> {
        profileManager.isLoading.value = true;
        try {
            await Promise.all([
                relationshipTypesStore.fetchRelationshipTypes(),
                relationshipTypesStore.fetchConfigRelationshipTypes(),
                fetchActiveTemplates(),
                profileManager.fetchData(),
            ]);
        } catch (error) {
            console.error('Failed to initially load profile data:', error);
            profileManager.profileGroups.value = [];
        } finally {
            profileManager.isLoading.value = false;
        }
    }

    async function createProfileAndShow(payload: any): Promise<ProfileGroup | undefined> {
        const newRiskModel = await api.accountAdmin.createRiskModel(payload);
        const newGroup: ProfileGroup = {
            profileGroupId: newRiskModel.id,
            profileName: newRiskModel.model_name,
            entity_type: newRiskModel.entity_type,
            isActive: newRiskModel.is_active,
            createdAt: newRiskModel.inserted_at,
            last_updated_at: newRiskModel.updated_at,
            instances: newRiskModel.instances,
            currentInstanceId: newRiskModel.current_kyx_risk_model_instance_id,
            relationship_types: newRiskModel.relationship_types,
            default_outdated_threshold_value: newRiskModel.default_outdated_threshold_value,
            default_outdated_threshold_type: newRiskModel.default_outdated_threshold_type,
        };

        profileManager.profileGroups.value.unshift(newGroup);
        profileManager.navigateToProfileDetails(newGroup);
        return newGroup;
    }


    return {
        // Pass through the entire profile manager
        ...profileManager,

        // Overrides and additions for this specific store
        fetchData: initialLoad, // Override with the extended initial load
        saveConfiguration: profileManager.saveNewProfile,
        navigateToAddProfile: profileManager.navigateToAddProfile,

        // Specific state for this store
        activeTemplates,

        // Specific actions for this store
        createProfileAndShow,
    };
});
